import React from "react";
import { Link } from "react-router-dom";

import {
  articleJsonLd,
  Breadcrumbs,
  DemenagementLayout,
  DictimmoCta,
  InternalLinks,
  SourceList,
  makeBreadcrumbs,
} from "./DemenagementCommon";

const path = "/guides/preavis-location-1-mois-ou-3-mois";
const breadcrumbs = makeBreadcrumbs([
  { name: "Déménagement", path: "/demenagement" },
  { name: "Préavis location : 1 mois ou 3 mois ?", path },
]);

const sources = [
  {
    label: "Service-public : préavis et congé du locataire",
    href: "https://www.service-public.fr/particuliers/vosdroits/F1168",
  },
  {
    label: "Service-public : simulateur de délai de préavis du locataire",
    href: "https://www.service-public.fr/simulateur/calcul/delaiPreavisLocataire",
  },
  {
    label: "ANIL : résiliation du bail",
    href: "https://www.anil.org/parole-expert-logement-location/parole-expert-logement-location-resiliation-bail/",
  },
];

const sections = [
  ["location-meublee", "Location meublée"],
  ["location-vide", "Location vide"],
  ["zone-tendue", "Zone tendue"],
  ["mutation-perte-emploi", "Mutation, perte d'emploi, nouvel emploi"],
  ["sante", "Raisons de santé"],
  ["rsa-aah", "RSA / AAH"],
  ["logement-social", "Attribution d'un logement social"],
  ["depart-preavis", "Date de départ du préavis"],
  ["calcul-date-fin-preavis", "Calculer la date de fin de préavis"],
  ["erreurs", "Erreurs fréquentes"],
];

export default function GuidePreavisLocation() {
  const guideJsonLd = articleJsonLd({
    headline: "Préavis location : 1 mois ou 3 mois ?",
    description: "Guide pratique pour comprendre les principaux cas de préavis location : meublé, vide, zone tendue, mutation, emploi, santé, RSA, AAH et logement social.",
    path,
    keywords: [
      "préavis location 1 mois",
      "préavis location 3 mois",
      "zone tendue",
      "lettre préavis location",
      "date fin préavis location",
    ],
  });

  return (
    <DemenagementLayout
      title="Préavis location : 1 mois ou 3 mois ?"
      description="Location vide ou meublée, zone tendue, mutation, santé, RSA, AAH, logement social : les grands cas à vérifier pour connaître son délai de préavis."
      path={path}
      breadcrumbs={breadcrumbs}
      label="Guide pratique"
      jsonLd={[guideJsonLd]}
    >
      <Breadcrumbs items={breadcrumbs} />

      <article className="move-article">
        <header className="move-hero move-hero-single">
          <div>
            <h1>Préavis location : 1 mois ou 3 mois ?</h1>
            <p>
              Le délai dépend du type de location et de certaines situations
              particulières. Cette page donne un repère général pour préparer
              votre courrier, sans remplacer les sources officielles ni un conseil
              juridique adapté à votre cas.
            </p>
          </div>
        </header>

        <section className="move-answer-box" aria-labelledby="reponse-rapide">
          <h2 id="reponse-rapide">Réponse rapide</h2>
          <ul>
            <li>Location meublée : le préavis est généralement d'un mois.</li>
            <li>Location vide : le préavis est généralement de trois mois.</li>
            <li>Préavis réduit à un mois : certains cas peuvent le permettre, à vérifier avec justificatifs si nécessaire.</li>
          </ul>
        </section>

        <div className="move-article-content">
          <div className="move-article-body">
            <section id="location-meublee">
              <h2>Location meublée</h2>
              <p>
                Pour un logement loué meublé à titre de résidence principale, le
                préavis donné par le locataire est généralement d'un mois. Le
                courrier doit être transmis par un mode permettant de prouver la
                date de réception ou de remise.
              </p>
            </section>

            <section id="location-vide">
              <h2>Location vide</h2>
              <p>
                Pour une location vide, le délai de préavis est généralement de
                trois mois. Il peut toutefois être réduit à un mois dans certains
                cas prévus par les textes. Le bail, la commune et votre situation
                personnelle peuvent donc changer l'analyse.
              </p>
            </section>

            <section id="zone-tendue">
              <h2>Zone tendue</h2>
              <p>
                Si le logement est situé dans une commune en zone tendue, le
                locataire peut généralement bénéficier d'un préavis réduit. La
                qualification de la commune doit être vérifiée sur une source
                officielle avant l'envoi du courrier.
              </p>
            </section>

            <section id="mutation-perte-emploi">
              <h2>Mutation, perte d'emploi, nouvel emploi</h2>
              <p>
                Une mutation professionnelle, une perte d'emploi ou un nouvel
                emploi consécutif à une perte d'emploi peuvent ouvrir droit à un
                préavis réduit dans les conditions prévues par les textes. Conservez
                les documents justificatifs et vérifiez que votre situation entre
                bien dans le cas concerné.
              </p>
            </section>

            <section id="sante">
              <h2>Raisons de santé</h2>
              <p>
                Certains motifs liés à l'état de santé peuvent permettre un délai
                réduit lorsqu'un changement de domicile est justifié. La formulation
                du courrier doit rester sobre et les justificatifs doivent être
                adaptés à la situation.
              </p>
            </section>

            <section id="rsa-aah">
              <h2>RSA / AAH</h2>
              <p>
                Les bénéficiaires du RSA ou de l'AAH font partie des situations à
                vérifier pour un préavis réduit. Le courrier doit annoncer le motif
                et les pièces utiles doivent être jointes lorsque les règles
                applicables l'exigent.
              </p>
            </section>

            <section id="logement-social">
              <h2>Attribution d'un logement social</h2>
              <p>
                L'attribution d'un logement social peut également être un motif de
                préavis réduit. Vérifiez les conditions exactes et conservez la
                preuve de l'attribution avant d'envoyer votre congé.
              </p>
            </section>

            <section id="depart-preavis">
              <h2>Date de départ du préavis</h2>
              <p>
                Le délai ne démarre pas simplement à la date de rédaction. Il est
                généralement lié à la réception de la lettre, à la remise en main
                propre contre preuve, ou à la signification par commissaire de
                justice. C'est l'une des raisons pour lesquelles le mode d'envoi
                compte beaucoup.
              </p>
            </section>

            <section id="calcul-date-fin-preavis">
              <h2>Calculer la date de fin de préavis</h2>
              <p>
                La date de fin de préavis dépend du délai applicable et de la date
                qui fait courir ce délai. Pour une recherche du type "calcul date
                fin préavis location", le bon réflexe est donc de vérifier d'abord
                si votre logement relève d'un préavis d'un mois ou de trois mois,
                puis de confirmer le point de départ avec les sources officielles.
              </p>
              <p>
                En pratique, conservez la preuve de réception ou de remise du
                congé, car elle permet souvent de déterminer la fin théorique du
                préavis et d'organiser l'état des lieux de sortie.
              </p>
            </section>

            <section id="erreurs">
              <h2>Erreurs fréquentes</h2>
              <ul>
                <li>Envoyer un courrier sans preuve de réception ou de remise.</li>
                <li>Demander un préavis réduit sans vérifier le motif exact.</li>
                <li>Oublier les justificatifs nécessaires.</li>
                <li>Confondre la date d'envoi et la date de départ du préavis.</li>
                <li>Ne pas anticiper l'état des lieux de sortie et la remise des clés.</li>
              </ul>
            </section>

            <div className="move-actions no-print">
              <Link className="move-btn move-btn-primary" to="/outils/lettre-preavis-location">
                Générer ma lettre de préavis
              </Link>
              <Link className="move-btn move-btn-secondary" to="/checklists/checklist-demenagement-pdf">
                Préparer ma checklist
              </Link>
            </div>
          </div>

          <aside className="move-toc no-print" aria-label="Sommaire">
            <h2>Sommaire</h2>
            {sections.map(([id, title]) => (
              <a href={`#${id}`} key={id}>
                {title}
              </a>
            ))}
          </aside>
        </div>
      </article>

      <DictimmoCta
        title="Vous préparez une nouvelle adresse ?"
        text="Avant de signer ou de déménager, consultez les prix du secteur, les risques, l'environnement, la météo et les données de la commune."
        buttonLabel="Analyser ma future adresse"
        eventName="dictimmo_cta_clicked_from_guide_preavis"
      />

      <InternalLinks currentPath={path} />
      <SourceList sources={sources} />
    </DemenagementLayout>
  );
}
